import { useEffect, useMemo, useState } from "react";
import { createNote, getAllNotes, updateNote } from "../notes/NoteService";
import { addExpense } from "../expense/transactions/TransactionService";
import { getAllAccounts } from "../expense/accounts/AccountService";
import { useToast } from "../ToastContext";
import "./Journal.css";

const ENTRY_TYPES = [
  { value: "NOTE",    label: "Note",    icon: "📝" },
  { value: "FOOD",    label: "Food",    icon: "🍽️" },
  { value: "EXPENSE", label: "Expense", icon: "💸" },
  { value: "WORK",    label: "Work",    icon: "💼" },
  { value: "MOOD",    label: "Mood",    icon: "😊" },
  { value: "HEALTH",  label: "Health",  icon: "💪" },
  { value: "LEARN",   label: "Learn",   icon: "📚" },
  { value: "GRATITUDE", label: "Gratitude", icon: "🙏" },
];

const MOOD_OPTIONS = [
  { value: 5, label: "Great",   icon: "😄" },
  { value: 4, label: "Good",    icon: "🙂" },
  { value: 3, label: "Okay",    icon: "😐" },
  { value: 2, label: "Low",     icon: "😕" },
  { value: 1, label: "Rough",   icon: "😣" },
];

const PROMPTS = [
  "What was the highlight of your day?",
  "What are you grateful for today?",
  "What did you learn today?",
  "What was challenging? How did you handle it?",
  "What would you do differently?",
  "What are you looking forward to tomorrow?",
];

function toDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}`;
}
function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
}
function parseContent(content) {
  if (!content) return { entries: [], mood: null };
  try {
    const p = JSON.parse(content);
    return { entries: Array.isArray(p?.entries) ? p.entries : [], mood: p?.mood ?? null };
  } catch { return { entries: [], mood: null }; }
}
function buildSummary(entries, mood) {
  const counts = entries.reduce((acc, e) => { acc[e.kind] = (acc[e.kind]||0)+1; return acc; }, {});
  const parts = Object.entries(counts).map(([k,v]) => `${k}:${v}`);
  if (mood) parts.unshift(`Mood:${mood}`);
  return parts.join(", ");
}

export default function Journal() {
  const toast = useToast();
  const todayKey = useMemo(() => toDateKey(new Date()), []);
  const [viewDate, setViewDate] = useState(todayKey);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allJournals, setAllJournals] = useState([]);
  const [journalNote, setJournalNote] = useState(null);
  const [entries, setEntries] = useState([]);
  const [mood, setMood] = useState(null);
  const [searchQ, setSearchQ] = useState("");

  // form state
  const [type, setType] = useState("NOTE");
  const [time, setTime] = useState(nowHHMM());
  const [text, setText] = useState("");
  const [amount, setAmount] = useState("");
  const [promptIdx, setPromptIdx] = useState(0);

  // finance link state
  const [accounts, setAccounts] = useState([]);
  const [financeLink, setFinanceLink] = useState(null); // { entryId, amount, description } waiting for account pick
  const [linkingAccountId, setLinkingAccountId] = useState("");
  const [linkingSaving, setLinkingSaving] = useState(false);

  const isToday = viewDate === todayKey;

  // Load all journals for history/search
  const loadAllJournals = async () => {
    const all = await getAllNotes();
    const journals = (all||[]).filter(n => n.noteType === "JOURNAL" && n.status !== "Deleted");
    setAllJournals(journals.sort((a,b) => (b.createdDate||"").localeCompare(a.createdDate||"")));
  };

  // Load specific date's journal
  const loadJournal = async (dateKey) => {
    setLoading(true);
    try {
      const all = await getAllNotes();
      const found = (all||[]).find(n =>
        n.noteType === "JOURNAL" &&
        n.createdDate === dateKey &&
        n.status !== "Deleted"
      );
      if (found) {
        const { entries: e, mood: m } = parseContent(found.content);
        setJournalNote(found);
        setEntries(e);
        setMood(m);
      } else if (dateKey === todayKey) {
        // Auto-create today's journal
        const created = await createNote({
          title: `Journal ${dateKey}`,
          content: JSON.stringify({ entries: [], mood: null }),
          noteType: "JOURNAL",
          priority: 3, status: "ACTIVE",
        });
        setJournalNote(created);
        setEntries([]);
        setMood(null);
      } else {
        setJournalNote(null);
        setEntries([]);
        setMood(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllJournals();
    getAllAccounts().then(a => setAccounts(a || []));
  }, []);
  useEffect(() => { loadJournal(viewDate); }, [viewDate]); // eslint-disable-line

  const persist = async (nextEntries, nextMood) => {
    if (!journalNote?.id) return;
    setSaving(true);
    try {
      await updateNote(journalNote.id, {
        title: `Journal ${viewDate}`,
        content: JSON.stringify({ entries: nextEntries, mood: nextMood }),
        noteType: "JOURNAL",
        priority: 3, status: "ACTIVE",
        noteSummary: buildSummary(nextEntries, nextMood),
      });
      setEntries(nextEntries);
      setMood(nextMood);
    } catch { toast("Failed to save.", "error"); }
    setSaving(false);
  };

  const addEntry = async () => {
    const clean = text.trim();
    if (!clean || saving) return;
    const item = {
      id: `jr-${Date.now()}-${Math.random().toString(16).slice(2,7)}`,
      kind: type, time: time || nowHHMM(), text: clean,
      amount: type === "EXPENSE" && amount ? Number(amount) : null,
    };
    const next = [...entries, item].sort((a,b) => a.time > b.time ? 1 : -1);
    await persist(next, mood);
    setText(""); setAmount(""); setTime(nowHHMM());
    toast("Entry added", "success");
  };

  const removeEntry = async (id) => {
    if (saving) return;
    await persist(entries.filter(e => e.id !== id), mood);
  };

  const startFinanceLink = (entry) => {
    setFinanceLink(entry);
    setLinkingAccountId(accounts[0]?.id || "");
  };

  const confirmFinanceLink = async () => {
    if (!financeLink || !linkingAccountId) return;
    setLinkingSaving(true);
    try {
      const res = await addExpense({
        amount: financeLink.amount,
        description: financeLink.text,
        transactionDate: viewDate,
        accountId: linkingAccountId,
        transactionType: "EXPENSE",
      });
      if (res.ok) {
        toast("Added to Finance ✓", "success");
        setFinanceLink(null);
      } else {
        toast("Failed to add to Finance", "error");
      }
    } catch {
      toast("Failed to add to Finance", "error");
    } finally {
      setLinkingSaving(false);
    }
  };

  const handleMood = async (val) => {
    const nextMood = val === mood ? null : val; // toggle off
    await persist(entries, nextMood);
  };

  const changeDate = (delta) => {
    const d = new Date(viewDate + "T00:00:00");
    d.setDate(d.getDate() + delta);
    const newKey = toDateKey(d);
    if (newKey <= todayKey) setViewDate(newKey);
  };

  // Calendar dates that have a journal
  // const journalDates = new Set(allJournals.map(j => j.createdDate)); // reserved for calendar heatmap

  // Search results
  const searchResults = searchQ.trim()
    ? allJournals.filter(j => {
        const { entries: e } = parseContent(j.content);
        return e.some(entry => entry.text?.toLowerCase().includes(searchQ.toLowerCase()));
      })
    : [];

  const kindIcon = (kind) => ENTRY_TYPES.find(t => t.value === kind)?.icon || "📝";

  return (
    <div className="jrnl-wrap">
      <div className="jrnl-header">
        <div>
          <h2 className="jrnl-title">Journal</h2>
          <p className="jrnl-subtitle">Private daily log — thoughts, expenses, moods, wins.</p>
        </div>
        <div className="jrnl-nav">
          <button className="jrnl-nav-btn" onClick={() => changeDate(-1)}>‹</button>
          <input
            type="date"
            className="jrnl-date-pick"
            value={viewDate}
            max={todayKey}
            onChange={e => setViewDate(e.target.value)}
          />
          <button className="jrnl-nav-btn" onClick={() => changeDate(1)} disabled={viewDate >= todayKey}>›</button>
          {viewDate !== todayKey && (
            <button className="jrnl-today-btn" onClick={() => setViewDate(todayKey)}>Today</button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="jrnl-search-row">
        <input
          className="jrnl-search"
          placeholder="Search journal entries…"
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
        />
        {searchQ && <button className="jrnl-search-clear" onClick={() => setSearchQ("")}>×</button>}
      </div>

      {searchQ && (
        <div className="jrnl-search-results">
          {searchResults.length === 0
            ? <p className="jrnl-muted">No results for "{searchQ}"</p>
            : searchResults.map(j => {
                const { entries: e } = parseContent(j.content);
                const matched = e.filter(en => en.text?.toLowerCase().includes(searchQ.toLowerCase()));
                return (
                  <div key={j.id} className="jrnl-search-group">
                    <button className="jrnl-search-date" onClick={() => { setViewDate(j.createdDate); setSearchQ(""); }}>
                      {j.createdDate}
                    </button>
                    {matched.map((en, i) => (
                      <div key={i} className="jrnl-search-entry">
                        <span>{kindIcon(en.kind)}</span>
                        <span>{en.text}</span>
                      </div>
                    ))}
                  </div>
                );
              })
          }
        </div>
      )}

      {!searchQ && (
        <>
          {loading && <p className="jrnl-muted">Loading…</p>}

          {!loading && (
            <>
              {/* Mood picker */}
              <div className="jrnl-mood-row">
                <span className="jrnl-mood-label">How are you feeling?</span>
                <div className="jrnl-mood-options">
                  {MOOD_OPTIONS.map(m => (
                    <button
                      key={m.value}
                      className={`jrnl-mood-btn ${mood === m.value ? "active" : ""}`}
                      onClick={() => isToday && handleMood(m.value)}
                      title={m.label}
                      disabled={!isToday}
                    >
                      {m.icon}
                    </button>
                  ))}
                </div>
                {mood && <span className="jrnl-mood-chosen">{MOOD_OPTIONS.find(m => m.value === mood)?.label}</span>}
              </div>

              {/* Prompt of the day */}
              {isToday && (
                <div className="jrnl-prompt-row">
                  <span className="jrnl-prompt-icon">💭</span>
                  <span className="jrnl-prompt-text">{PROMPTS[promptIdx]}</span>
                  <button className="jrnl-prompt-next" onClick={() => setPromptIdx(i => (i+1) % PROMPTS.length)}>
                    Next prompt
                  </button>
                  <button className="jrnl-prompt-use" onClick={() => { setType("NOTE"); setText(PROMPTS[promptIdx] + " "); }}>
                    Use
                  </button>
                </div>
              )}

              {/* Add entry form */}
              {isToday && (
                <div className="jrnl-form">
                  <div className="jrnl-type-row">
                    {ENTRY_TYPES.map(t => (
                      <button key={t.value}
                        className={`jrnl-type-btn ${type === t.value ? "active" : ""}`}
                        onClick={() => setType(t.value)}
                        title={t.label}
                      >
                        {t.icon} {t.label}
                      </button>
                    ))}
                  </div>
                  <div className="jrnl-input-row">
                    <input type="time" value={time} onChange={e => setTime(e.target.value)} className="jrnl-time-input" />
                    <input
                      className="jrnl-text-input"
                      value={text}
                      onChange={e => setText(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && !e.shiftKey && addEntry()}
                      placeholder={type === "MOOD" ? "Add a mood note…" : type === "GRATITUDE" ? "What are you grateful for?" : "What happened?"}
                    />
                    {type === "EXPENSE" && (
                      <input type="number" className="jrnl-amount-input" value={amount} onChange={e => setAmount(e.target.value)} placeholder="₹ Amount" />
                    )}
                    <button className="jrnl-add-btn" onClick={addEntry} disabled={saving || !text.trim()}>
                      {saving ? "…" : "Add"}
                    </button>
                  </div>
                </div>
              )}

              {/* Entry list */}
              <div className="jrnl-entries">
                {entries.length === 0 && !isToday && (
                  <p className="jrnl-muted">No entries for {viewDate}.</p>
                )}
                {entries.length === 0 && isToday && (
                  <p className="jrnl-muted">Start logging your day above.</p>
                )}
                {entries.map(e => (
                  <div key={e.id} className={`jrnl-entry jrnl-entry-${(e.kind||"NOTE").toLowerCase()}`}>
                    <div className="jrnl-entry-left">
                      <span className="jrnl-entry-icon">{kindIcon(e.kind)}</span>
                      <div>
                        <div className="jrnl-entry-text">{e.text}</div>
                        {e.amount != null && (
                          <div className="jrnl-entry-amount-row">
                            <span className="jrnl-entry-amount">₹{e.amount}</span>
                            {e.kind === "EXPENSE" && accounts.length > 0 && (
                              <button
                                className="jrnl-finance-link-btn"
                                onClick={() => startFinanceLink(e)}
                                title="Add to Finance tracker"
                              >
                                → Finance
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="jrnl-entry-right">
                      <span className="jrnl-entry-time">{e.time}</span>
                      {isToday && (
                        <button className="jrnl-entry-remove" onClick={() => removeEntry(e.id)} disabled={saving}>×</button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Finance link modal */}
                {financeLink && (
                  <div className="jrnl-finance-modal">
                    <div className="jrnl-finance-modal-inner">
                      <div className="jrnl-finance-modal-title">Add ₹{financeLink.amount} to Finance</div>
                      <div className="jrnl-finance-modal-desc">"{financeLink.text}"</div>
                      <label className="jrnl-finance-modal-label">
                        Account
                        <select
                          value={linkingAccountId}
                          onChange={e => setLinkingAccountId(e.target.value)}
                          className="jrnl-finance-modal-select"
                        >
                          {accounts.map(a => (
                            <option key={a.id} value={a.id}>{a.accountName}</option>
                          ))}
                        </select>
                      </label>
                      <div className="jrnl-finance-modal-actions">
                        <button
                          className="jrnl-finance-modal-confirm"
                          onClick={confirmFinanceLink}
                          disabled={linkingSaving}
                        >
                          {linkingSaving ? "Adding…" : "Add expense"}
                        </button>
                        <button
                          className="jrnl-finance-modal-cancel"
                          onClick={() => setFinanceLink(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Past journal date picker */}
              {allJournals.length > 0 && (
                <div className="jrnl-history">
                  <div className="jrnl-history-title">Journal history</div>
                  <div className="jrnl-history-list">
                    {allJournals.slice(0, 14).map(j => {
                      const { mood: m } = parseContent(j.content);
                      const moodIcon = m ? MOOD_OPTIONS.find(mo => mo.value === m)?.icon : null;
                      return (
                        <button key={j.id}
                          className={`jrnl-history-btn ${j.createdDate === viewDate ? "active" : ""}`}
                          onClick={() => setViewDate(j.createdDate)}
                        >
                          <span>{j.createdDate?.slice(5)}</span>
                          {moodIcon && <span>{moodIcon}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
